import type { PosDatabase } from '@core/db/db'
import type { AssetRecord } from '@core/db/models'

export type AssetOwnerType = 'category' | 'product'

/**
 * Локальный кэш бинарных ассетов (загруженные картинки).
 * Ключ `category:<name>` / `product:<id>` уникален.
 */
export class AssetService {
  constructor(private readonly db: PosDatabase) {}

  private key(ownerType: AssetOwnerType, ownerKey: string): string {
    return `${ownerType}:${ownerKey}`
  }

  async put(
    ownerType: AssetOwnerType,
    ownerKey: string,
    blob: Blob,
    mime: string
  ): Promise<void> {
    const key = this.key(ownerType, ownerKey)
    const existing = await this.db.assets.where('key').equals(key).first()
    const record: AssetRecord = {
      key,
      ownerType,
      ownerKey,
      mime,
      blob,
      updatedAt: Date.now()
    }
    if (existing?.id != null) {
      await this.db.assets.update(existing.id, record)
    } else {
      await this.db.assets.add(record)
    }
  }

  async get(
    ownerType: AssetOwnerType,
    ownerKey: string
  ): Promise<{ blob: Blob; mime: string } | null> {
    const row = await this.db.assets
      .where('key')
      .equals(this.key(ownerType, ownerKey))
      .first()
    return row ? { blob: row.blob, mime: row.mime } : null
  }

  async remove(ownerType: AssetOwnerType, ownerKey: string): Promise<void> {
    const row = await this.db.assets
      .where('key')
      .equals(this.key(ownerType, ownerKey))
      .first()
    if (row?.id != null) {
      await this.db.assets.delete(row.id)
    }
  }

  putCategoryImage(name: string, blob: Blob, mime: string): Promise<void> {
    return this.put('category', name, blob, mime)
  }

  getCategoryImage(name: string): Promise<{ blob: Blob; mime: string } | null> {
    return this.get('category', name)
  }

  removeCategoryImage(name: string): Promise<void> {
    return this.remove('category', name)
  }
}
